/**
 * The agents and the instructions they run on. Every request goes to the AI Agent, which answers questions itself and
 * hands work to the ten subagents of the roster (`roles.ts`) through `delegate`; the local agent (Claude Code) works
 * alone with every tool.
 */
import type { Catalog } from './agentCatalog';
import type { MaterialEntry } from '../state/materials';
import { RENDER_STYLES } from '../state/aiRender';
import { fmt } from './sceneSummary';
import { TOOL_DEFINITIONS, toolsInGroups } from './toolSchemas';
import { ROLES } from './roles';
import { usableCatalogItems } from './catalogFilter';
import { language, t, type LanguageCode } from '../i18n/I18n';

export type Effort = 'low' | 'medium' | 'high' | 'xhigh' | 'max';

export interface AgentDefinition {
  id: string;
  name: string;
  description: string;
  model: string;
  effort?: Effort;
  tools: readonly string[];
  /** Role guidance appended to the shared instructions. */
  role: string;
}

export const DEFAULT_MODEL = 'claude-opus-5';
export const MODEL_CHOICES: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'claude-opus-5', label: 'Claude Opus 5' },
  { id: 'claude-sonnet-5', label: 'Claude Sonnet 5' },
  { id: 'claude-haiku-4-5', label: 'Claude Haiku 4.5' },
  { id: 'claude-fable-5-1', label: 'Claude Fable 5.1' },
];

const AI_AGENT_ROLE = 'You are the AI Agent, the one the user talks to. You do not edit the project yourself: you hand the work to the subagents in the list below with delegate. A small task goes to the one subagent whose row it is; split a big request into one task per subagent and hand them out in one delegate call, so they work side by side — never two tasks for one subagent in a call. Order what depends on something else (the plan before the furniture, the furniture before its finishes or a render) across calls. Each brief names its room and what to leave alone. A subagent reporting a task is not its job means it went to the wrong row: hand it to the right one. Answer questions about the project yourself from the map, changing nothing. Some things no agent can do yet: measured plans and estimate documents, payments, access to the project — say so plainly instead of trying.';

/** The one agent a user talks to: it routes the work and changes nothing itself. */
export const AI_AGENT: AgentDefinition = {
  id: 'ai-agent', get name() { return t('#AgentPanelAiAgent'); }, get description() { return t('#AgentAiAgentDescription'); }, model: DEFAULT_MODEL,
  tools: toolsInGroups(['read', 'view', 'history', 'project', 'agents']).map(tool => tool.name), role: AI_AGENT_ROLE,
};

/** The local agent (Claude Code) has no subagents: it does the work itself, with every tool. */
export const LOCAL_AGENT: AgentDefinition = {
  id: AI_AGENT.id, get name() { return t('#AgentPanelAiAgent'); }, get description() { return t('#AgentAiAgentDescription'); }, model: DEFAULT_MODEL,
  tools: TOOL_DEFINITIONS.filter(tool => tool.name !== 'delegate').map(tool => tool.name),
  role: 'You are the AI Agent and you do the work yourself: plan rooms, place and arrange furniture, choose finishes, manage the view and make renders.',
};

/** The roster as agents the AI Agent can delegate to; a role's description is its line in the AI Agent's list. */
export const ROLE_AGENTS: readonly AgentDefinition[] = ROLES.map(role => ({
  id: role.id, get name() { return role.name; }, description: role.brief, model: DEFAULT_MODEL, tools: role.tools, role: role.prompt,
}));

export interface AgentSettings {
  /** Overrides the model of the AI Agent and its subagents when set. */
  model?: string;
  /** The user's own Anthropic key: the browser then talks to the API directly. */
  apiKey?: string;
  /** Accept tool calls from external agents over the local MCP bridge. */
  bridge: boolean;
  /** Answer from a scripted stand-in instead of a model (UI checks). */
  mock?: boolean;
  /** Run through the local agent — `npm run agent:local`, Claude Code on the user's subscription. */
  localAgent: boolean;
  localAgentUrl?: string;
  /** Model alias handed to `claude --model`; empty keeps Claude Code's own default. */
  localModel?: string;
  /** Which API the built-in agents talk to: Claude, or an OpenAI-compatible one (DeepSeek and the like). */
  provider: ProviderKind;
  /** True once the user picked a provider; until then the editor follows the keys its server holds. */
  providerChosen?: boolean;
  /** The OpenAI-compatible endpoint; without a key the editor's proxy supplies the server's. */
  openai?: { baseUrl?: string; apiKey?: string; model?: string; /** Let a DeepSeek model think before answering (slower). */ thinking?: boolean };
}

export type ProviderKind = 'claude' | 'openai';
export const PROVIDER_CHOICES: ReadonlyArray<{ id: ProviderKind; label: string }> = [
  { id: 'claude', label: 'Claude' },
  { id: 'openai', get label() { return t('#AgentProviderOpenAICompatible'); } },
];

export const LOCAL_AGENT_URL = 'http://127.0.0.1:5181';
export const LOCAL_MODEL_CHOICES: ReadonlyArray<{ id: string; label: string }> = [
  { id: '', get label() { return t('#AgentLocalModelDefault'); } },
  { id: 'opus', label: 'Opus' },
  { id: 'sonnet', label: 'Sonnet' },
  { id: 'haiku', label: 'Haiku' },
];

const SETTINGS_KEY = 'hp.agent.settings';
const DEFAULT_SETTINGS: AgentSettings = { bridge: false, localAgent: false, provider: 'claude' };

export function loadAgentSettings(storage: Pick<Storage, 'getItem'> | null = typeof localStorage === 'undefined' ? null : localStorage): AgentSettings {
  try {
    const raw = storage?.getItem(SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const parsed = JSON.parse(raw) as Partial<AgentSettings>;
    return {
      model: typeof parsed.model === 'string' && parsed.model ? parsed.model : undefined,
      apiKey: typeof parsed.apiKey === 'string' && parsed.apiKey ? parsed.apiKey : undefined,
      bridge: parsed.bridge === true,
      mock: parsed.mock === true,
      localAgent: parsed.localAgent === true,
      localAgentUrl: typeof parsed.localAgentUrl === 'string' && parsed.localAgentUrl ? parsed.localAgentUrl : undefined,
      localModel: typeof parsed.localModel === 'string' && parsed.localModel ? parsed.localModel : undefined,
      provider: parsed.provider === 'openai' ? 'openai' : 'claude',
      providerChosen: parsed.providerChosen === true,
      openai: parsed.openai && typeof parsed.openai === 'object' ? {
        baseUrl: typeof parsed.openai.baseUrl === 'string' && parsed.openai.baseUrl ? parsed.openai.baseUrl : undefined,
        apiKey: typeof parsed.openai.apiKey === 'string' && parsed.openai.apiKey ? parsed.openai.apiKey : undefined,
        model: typeof parsed.openai.model === 'string' && parsed.openai.model ? parsed.openai.model : undefined,
        thinking: parsed.openai.thinking === true,
      } : undefined,
    };
  } catch { return { ...DEFAULT_SETTINGS }; }
}

export function saveAgentSettings(settings: AgentSettings, storage: Pick<Storage, 'setItem'> | null = typeof localStorage === 'undefined' ? null : localStorage): void {
  try { storage?.setItem(SETTINGS_KEY, JSON.stringify(settings)); } catch { /* private mode: settings live for the session */ }
}

/** The model an agent actually runs on: the settings' choice, else the agent's default. */
export function agentModel(agent: AgentDefinition, settings: AgentSettings): string {
  return settings.model || agent.model;
}

/** The interface languages as the model is told them. */
const LANGUAGE_NAMES: Record<LanguageCode, string> = { en: 'English', ru: 'Russian' };

// i18n-ignore: text for the model; «сделай кухню», «обставь спальню» are examples of what a person types
const sharedInstructions = (): string => `You are an agent inside Home Planner Web, a browser interior editor with a 2D plan and a 3D view. You act on the user's open project through tools; the user watches the editor change live and can undo every step.

The map. Every user message starts with the project map: each room with its walls by the side they close (N = top of the plan, smaller z; S = bottom; W = left; E = right), the openings cut into each wall and the items in the room. Objects have short names — w2 a wall, o1 an opening, i3 an item — and every tool takes those names. Units are metres; x runs right, z runs down. Rotation in degrees: 0 = the front faces S (+z), 90 = E, 180 = N, 270 = W; a piece against the N wall faces S (0°), against the S wall 180°, against the W wall 90°, against the E wall 270°. An offset "@" is metres from the wall's first printed point.

How to work — act in your first reply, every call you can already make in one go, no plans or step announcements in text (the transcript shows each call); at most one short line before the first call, the summary at the end:
- Do not call get_scene when the map already tells you what you need — that is nearly always. Reach for it only for finishes (detail full), another floor or a room the map truncated.
- A whole room («сделай кухню», «обставь спальню»): add_room if there is none (typical sizes: kitchen 3×4, bedroom 3.5×4, living 4×5, bathroom 2×2.5, hall 1.5×3 m; put it beside existing rooms so walls are shared) → add_opening for a door on the wall shared with the hall or a neighbour (at "start") and a window on an outer wall → furnish_room with the preset (furniture, rug, curtains, lamps, pictures, plants and tabletop decor in one call) → set_room_finish → set_view orbit. Four to six calls, all in one reply. A room that already holds the user's furniture: keep it and add only what is missing (place_along_wall, place_beside, add_item).
- Single pieces: place_along_wall (backs to a wall, in a row — a bed, a sofa, a kitchen run, a wardrobe in a corner), place_beside (a nightstand by the bed, a coffee table before the sofa, a chair before a desk), arrange_around (chairs around a table). add_item with x/z only when the user points at a spot. These tools keep doors clear, keep pieces inside the room and warn about overlaps; fix a warning with update_item, otherwise carry on.
- Finishes: set_room_finish with the user's words (either language); it searches and applies the first match. search_materials only to offer choices; set_material only for one wall side or one item part.
- Catalog: only the ids listed below exist; furnish_room picks them itself. Prefer items marked ready; a "box" item shows as a plain block. Sizes are w×d×h in metres. A kind the catalog lacks (hob, bar stool…): say so in one line and skip it — never put an unrelated item in its place.
- A room is finished when it has its main pieces, storage, lighting, textiles (a rug, curtains on every window) and decor (pictures, plants, things on the tables). furnish_room places all of that; after it, add what the user named and stop — do not re-furnish a room that already has furniture unless asked to replace it.
- Change only what the request is about; never clear a floor or delete the user's things unless asked. Prefer doing over asking: ask one short question only when the request cannot be carried out sensibly without an answer.
- Reply in the user's language (${LANGUAGE_NAMES[language()]} by default), briefly: what you did and one suggestion. No names or ids, no narration of calls.`;

const NO_CATALOG_NOTE = 'The catalog is unavailable right now, so no subagent can add or replace an item: hand out no such task, and say in one line that items cannot be added yet. The plan, moving what is placed and finishes still work.';
const VISION_NOTE = 'Pictures: after a whole-room job you may call capture_view once and fix what you see; otherwise skip it.';
const NO_VISION_NOTE = 'Pictures: your model does not see images — never call capture_view; tool results already report overlaps and moves.';

export interface PromptContext { catalog: Catalog; materials: readonly MaterialEntry[]; agents: readonly AgentDefinition[]; /** The model sees images (Claude, Claude Code); an OpenAI-compatible model gets a note instead of a picture. */ vision?: boolean }

/** The catalog as one compact block; it is stable between turns, so it sits in the cached prefix. */
export function catalogBlock(catalog: Catalog): string {
  if (!catalog.items.length) return 'Catalog: unavailable (search_catalog may still work later).';
  const lines = ['Catalog (id — title — w×d×h m — placement; "box" = no 3D model yet):'];
  const usable = usableCatalogItems(catalog.items);
  for (const section of catalog.sections) {
    const items = usable.filter(item => item.section === section.id);
    if (!items.length) continue;
    lines.push(`## ${section.id} — ${section.title}`);
    for (const item of items) lines.push(`${item.id} — ${item.title} — ${fmt(item.size[0])}×${fmt(item.size[2])}×${fmt(item.size[1])} — ${item.placement}${item.converted ? '' : ' — box'}`);
  }
  const orphans = usable.filter(item => !catalog.sections.some(section => section.id === item.section));
  if (orphans.length) { lines.push('## other'); for (const item of orphans) lines.push(`${item.id} — ${item.title} — ${fmt(item.size[0])}×${fmt(item.size[2])}×${fmt(item.size[1])} — ${item.placement}${item.converted ? '' : ' — box'}`); }
  return lines.join('\n');
}

export function materialsBlock(materials: readonly MaterialEntry[]): string {
  if (!materials.length) return 'Materials: the material library is not connected to the agent yet; finish surfaces with a "color".';
  const counts = new Map<string, number>();
  for (const entry of materials) { const key = `${entry.surface}/${entry.collection}`; counts.set(key, (counts.get(key) ?? 0) + 1); }
  return `Material library (${materials.length} finishes; find texture ids with search_materials): ${[...counts].map(([key, count]) => `${key} ${count}`).join(', ')}.`;
}

export function agentsBlock(agents: readonly AgentDefinition[], self: AgentDefinition): string {
  const others = agents.filter(agent => agent.id !== self.id);
  return others.length ? `Subagents you can delegate to (delegate tool, agent_id — name: what it may change): ${others.map(agent => `${agent.id} — ${agent.name}: ${agent.description}`).join('; ')}.` : '';
}

export function stylesBlock(): string {
  return `Render styles (render_ai style ids): ${RENDER_STYLES.map(style => `${style.id} (${style.label})`).join(', ')}.`;
}

/** The system prompt as text blocks; the last one carries the cache breakpoint. */
export function buildSystemBlocks(agent: AgentDefinition, context: PromptContext): Array<{ type: 'text'; text: string; cache_control?: { type: 'ephemeral' } }> {
  const canDelegate = agent.tools.includes('delegate');
  const canRender = agent.tools.includes('render_ai');
  const reference = [catalogBlock(context.catalog), materialsBlock(context.materials), canRender ? stylesBlock() : '', canDelegate ? agentsBlock(context.agents, agent) : '',
    canDelegate && !context.catalog.items.length ? NO_CATALOG_NOTE : ''].filter(Boolean).join('\n\n');
  return [
    { type: 'text', text: `${sharedInstructions()}\n${context.vision === false ? NO_VISION_NOTE : VISION_NOTE}\n\nYour role: ${agent.role}` },
    { type: 'text', text: reference, cache_control: { type: 'ephemeral' } },
  ];
}
